import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  AlertTriangle,
  Send,
  CheckCircle,
  Loader2,
  MessageSquare,
  Handshake,
  Scale,
  ShieldAlert,
  Users,
  FileText,
  ArrowRight,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dispute types
const DISPUTE_TYPES = [
  { value: 'order_issue', label: 'Problema con un ordine', description: 'Prodotto non conforme, non ricevuto, danneggiato' },
  { value: 'seller_behavior', label: 'Comportamento venditore', description: 'Comunicazione scorretta, mancate risposte' },
  { value: 'buyer_behavior', label: 'Comportamento acquirente', description: 'Recensioni false, richieste irragionevoli' },
  { value: 'payment_issue', label: 'Problema pagamento/rimborso', description: 'Rimborso non ricevuto, addebiti errati' },
  { value: 'other', label: 'Altro', description: 'Altre situazioni non elencate' }
];

// Resolution steps
const RESOLUTION_STEPS = [
  {
    icon: MessageSquare,
    title: 'Contatta la controparte',
    description: 'Prima di aprire una segnalazione, prova a risolvere il problema direttamente con l\'altra parte tramite la messaggistica interna.'
  },
  {
    icon: Handshake,
    title: 'Cerca un accordo',
    description: 'Spesso i malintesi si risolvono con una comunicazione chiara. Proponi una soluzione ragionevole.'
  },
  {
    icon: Scale,
    title: 'Segnala se necessario',
    description: 'Se non riesci a trovare un accordo, compila il form sottostante. Esamineremo la situazione con imparzialità.'
  }
];

export default function DisputesPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    disputeType: 'order_issue',
    orderNumber: '',
    otherParty: '',
    description: '',
    attemptedResolution: '',
    desiredOutcome: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.description) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Devi confermare di aver tentato di risolvere la situazione');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use the same contact endpoint with dispute-specific data
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          reason: 'dispute',
          subject: `[CONTROVERSIA] ${DISPUTE_TYPES.find(t => t.value === formData.disputeType)?.label || 'Segnalazione'}`,
          message: `
TIPO SEGNALAZIONE: ${DISPUTE_TYPES.find(t => t.value === formData.disputeType)?.label || formData.disputeType}

NUMERO ORDINE: ${formData.orderNumber || 'Non specificato'}

CONTROPARTE: ${formData.otherParty || 'Non specificata'}

DESCRIZIONE DEL PROBLEMA:
${formData.description}

TENTATIVI DI RISOLUZIONE GIÀ EFFETTUATI:
${formData.attemptedResolution || 'Non specificati'}

ESITO DESIDERATO:
${formData.desiredOutcome || 'Non specificato'}
          `.trim(),
          orderNumber: formData.orderNumber
        })
      });

      if (!response.ok) {
        throw new Error('Errore nell\'invio della segnalazione');
      }

      setIsSubmitted(true);
      toast.success('Segnalazione inviata con successo!');
    } catch (error) {
      console.error('Error submitting dispute form:', error);
      toast.error('Errore nell\'invio della segnalazione. Riprova più tardi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Segnalazione Inviata | Controversie Rarifex</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-amber-50/30 flex items-center justify-center px-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Segnalazione ricevuta</h1>
            <p className="text-slate-600 mb-6">
              Abbiamo ricevuto la tua segnalazione e la esamineremo con attenzione. 
              Ti contatteremo entro 48-72 ore lavorative all'indirizzo email indicato.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Nel frattempo, ti invitiamo a conservare eventuali prove (screenshot, messaggi, foto) 
              che potrebbero essere utili per la valutazione del caso.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => {
                  setIsSubmitted(false);
                  setAcceptedTerms(false);
                  setFormData({
                    name: '',
                    email: '',
                    disputeType: 'order_issue',
                    orderNumber: '',
                    otherParty: '',
                    description: '',
                    attemptedResolution: '',
                    desiredOutcome: ''
                  });
                }}
                variant="outline"
              >
                Nuova segnalazione
              </Button>
              <Link to="/">
                <Button>Torna alla Home</Button>
              </Link>
            </div>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Controversie e Segnalazioni | Rarifex</title>
        <meta name="description" content="Segnala comportamenti scorretti o problemi con ordini su Rarifex. Il nostro team esaminerà la situazione con imparzialità." />
        <meta name="keywords" content="controversie rarifex, segnalazioni, reclami, problemi ordini, assistenza" />
        <link rel="canonical" href="https://www.rarifex.com/controversie" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Scale className="w-4 h-4" />
                Risoluzione Controversie
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-6">
                Risolviamo insieme
              </h1>
              
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Crediamo che la maggior parte dei problemi possa essere risolta con il dialogo. 
                Siamo qui per aiutarti quando serve un intermediario imparziale.
              </p>
            </div>
          </div>
        </section>

        {/* RESOLUTION STEPS */}
        <section className="py-12 border-b border-slate-100">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
                Prima di segnalare, prova questi passi
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {RESOLUTION_STEPS.map((step, index) => (
                  <Card key={index} className="p-6 text-center relative">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 bg-[#008080] text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 mt-2">
                      <step.icon className="w-6 h-6 text-slate-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-slate-600">{step.description}</p>
                  </Card>
                ))}
              </div>
              
              {/* Info box */}
              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Perché è importante provare prima?</p>
                  <p>
                    La maggior parte delle situazioni nasce da incomprensioni. Un messaggio gentile e chiaro 
                    spesso risolve il problema più velocemente di una segnalazione formale. Inoltre, 
                    dimostrare di aver tentato una risoluzione amichevole è fondamentale per la valutazione del caso.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MAIN CONTENT */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <ShieldAlert className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Apri una segnalazione</h2>
                    <p className="text-sm text-slate-500">Compila il form per segnalare un problema</p>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name and Email row */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Il tuo nome *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Mario Rossi"
                        required
                        data-testid="dispute-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">La tua email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="mario@esempio.com"
                        required
                        data-testid="dispute-email-input"
                      />
                    </div>
                  </div>

                  {/* Dispute Type */}
                  <div className="space-y-2">
                    <Label htmlFor="disputeType">Tipo di segnalazione *</Label>
                    <select
                      id="disputeType"
                      name="disputeType"
                      value={formData.disputeType}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#008080] focus:border-transparent"
                      data-testid="dispute-type-select"
                    >
                      {DISPUTE_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500">
                      {DISPUTE_TYPES.find(t => t.value === formData.disputeType)?.description}
                    </p>
                  </div>

                  {/* Order number and Other party */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="orderNumber">Numero ordine (se applicabile)</Label>
                      <Input
                        id="orderNumber"
                        name="orderNumber"
                        value={formData.orderNumber}
                        onChange={handleChange}
                        placeholder="RF-2025-XXXX"
                        data-testid="dispute-order-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="otherParty">Nome utente controparte</Label>
                      <Input
                        id="otherParty"
                        name="otherParty"
                        value={formData.otherParty}
                        onChange={handleChange}
                        placeholder="Nome dell'artigiano o acquirente"
                        data-testid="dispute-other-party-input"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Descrizione del problema *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Descrivi in modo dettagliato cosa è successo, includendo date e fatti rilevanti..."
                      rows={5}
                      required
                      data-testid="dispute-description-input"
                    />
                    <p className="text-xs text-slate-500">
                      Sii il più specifico possibile. Include date, numeri di riferimento e una cronologia degli eventi.
                    </p>
                  </div>

                  {/* Attempted Resolution */}
                  <div className="space-y-2">
                    <Label htmlFor="attemptedResolution">Tentativi di risoluzione già effettuati</Label>
                    <Textarea
                      id="attemptedResolution"
                      name="attemptedResolution"
                      value={formData.attemptedResolution}
                      onChange={handleChange}
                      placeholder="Descrivi come hai provato a risolvere il problema con l'altra parte..."
                      rows={3}
                      data-testid="dispute-resolution-input"
                    />
                  </div>

                  {/* Desired Outcome */}
                  <div className="space-y-2">
                    <Label htmlFor="desiredOutcome">Esito desiderato</Label>
                    <Textarea
                      id="desiredOutcome"
                      name="desiredOutcome"
                      value={formData.desiredOutcome}
                      onChange={handleChange}
                      placeholder="Cosa vorresti ottenere dalla risoluzione di questa situazione?"
                      rows={2}
                      data-testid="dispute-outcome-input"
                    />
                  </div>

                  {/* Terms acceptance */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 text-[#008080] border-slate-300 rounded focus:ring-[#008080]"
                        data-testid="dispute-terms-checkbox"
                      />
                      <span className="text-sm text-slate-700">
                        Confermo di aver tentato di risolvere la situazione direttamente con la controparte 
                        prima di inviare questa segnalazione, e che le informazioni fornite sono veritiere. 
                        Comprendo che segnalazioni false o in malafede possono comportare conseguenze sul mio account.
                      </span>
                    </label>
                  </div>

                  {/* Submit button */}
                  <Button 
                    type="submit" 
                    className="w-full bg-[#008080] hover:bg-[#006666]"
                    disabled={isSubmitting || !acceptedTerms}
                    data-testid="dispute-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Invia segnalazione
                      </>
                    )}
                  </Button>
                </form>
              </Card>

              {/* Additional info */}
              <div className="mt-8 grid md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-[#008080] flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-slate-900 mb-1">Tempi di risposta</h3>
                      <p className="text-sm text-slate-600">
                        Esaminiamo ogni segnalazione con attenzione. Riceverai una risposta entro 48-72 ore lavorative.
                      </p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-[#008080] flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-slate-900 mb-1">Imparzialità</h3>
                      <p className="text-sm text-slate-600">
                        Il nostro team valuterà la situazione ascoltando entrambe le parti prima di prendere decisioni.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Link to other resources */}
              <div className="mt-8 text-center text-sm text-slate-600">
                <p>
                  Hai bisogno di assistenza generale?{' '}
                  <Link to="/contatti" className="text-[#008080] hover:underline">
                    Contattaci
                  </Link>
                  {' '}oppure consulta le{' '}
                  <Link to="/assistenza" className="text-[#008080] hover:underline">
                    FAQ
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
